import React, { useState, useCallback, useRef, useEffect } from "react";
import DocsCards from "./DocsCards";

export interface IHealthcareDocumentProp {
  documents?: any[];
  handleRequestDocument: (documentID: string) => void;
  highlightId?: string | null;
}

const mimeToExt: Record<string, string> = {
  "application/pdf": ".pdf",
  "application/msword": ".doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
  "application/vnd.ms-excel": ".xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
  "image/jpeg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "image/tiff": ".tiff",
  "text/plain": ".txt",
  "application/zip": ".zip",
};

const resolveFilename = (nombre: string, uri: string, mimeType: string): string => {
  if (/\.[a-zA-Z0-9]{1,5}$/.test(nombre)) return nombre;
  const uriExt = uri.split("?")[0].match(/(\.[a-zA-Z0-9]{1,5})$/)?.[1];
  if (uriExt) return `${nombre}${uriExt}`;
  const mimeExt = mimeToExt[mimeType.split(";")[0].trim()];
  return mimeExt ? `${nombre}${mimeExt}` : nombre;
};

const HealthcareDocumentBlock: React.FC<IHealthcareDocumentProp> = ({
  documents = [],
  handleRequestDocument,
  highlightId,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [animateTable, setAnimateTable] = useState(false);

  useEffect(() => {
    if (highlightId && documents.length > 0) {
      setAnimateTable(true);
      const timer = setTimeout(() => setAnimateTable(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightId, documents.length]);

  const handleDownload = useCallback(async (uri: string, nombre: string) => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = resolveFilename(nombre, uri, blob.type);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(uri, "_blank", "noopener,noreferrer");
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={animateTable ? "highlight-table-pulse" : ""}
    >
      <style>{`
        .highlight-table-pulse {
          background-color: rgba(6, 182, 187, 0.1);
          border-radius: 8px;
          animation: tablePulse 1s infinite;
          box-shadow: 0 0 8px rgba(6, 182, 187, 0.3);
        }
        @keyframes tablePulse {
          0%   { transform: scale(1); }
          50%  { transform: scale(1.008); }
          100% { transform: scale(1); }
        }
      `}</style>

      <DocsCards
        items={documents}
        getName={(d) => d.nombre ?? ""}
        getDate={(d) => d.fecha}
        onDownload={(d) =>
          d.requiereOtp === false
            ? handleDownload(d.uri, d.nombre)
            : handleRequestDocument(d.id)
        }
        emptyMessage="No hay documentos asistenciales."
      />
    </div>
  );
};

export default HealthcareDocumentBlock;
